import { Link } from "react-router-dom";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import useSeo from "../hooks/useSeo";

// Required by App Store Guideline 3.1.2(c) for apps with auto-renewing
// subscriptions. Apple expects a functional Terms of Use (EULA) link both
// inside the app's purchase flow (PricingPage links here) and in the App
// Store description. We use Apple's standard Licensed Application End User
// License Agreement so we don't have to maintain a custom EULA.
const TermsOfUsePage = () => {
  useSeo({
    title: "Terms of Use — Bilateral Calm",
    description: "Terms of Use (EULA) for Bilateral Calm subscriptions.",
  });

  return (
    <div className="min-h-screen bg-[#FBF8F1]">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-16" data-testid="terms-of-use-page">
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl text-[#1B2349] mb-3">
          Terms of Use
        </h1>
        <p className="text-sm uppercase tracking-widest text-[#718096] mb-10">
          End User License Agreement
        </p>

        <section className="prose prose-slate text-[#2D3748] leading-relaxed space-y-5 text-[16px]">
          <p data-testid="terms-entity-statement">
            <strong>Bilateral Calm</strong> is operated by{" "}
            <strong>Mood and Sleep Clinic LLC</strong>, a limited liability company. In these
            Terms, "we," "us," and "Bilateral Calm" refer to Mood and Sleep Clinic LLC.
          </p>
          <p>
            By downloading, installing, or using <strong>Bilateral Calm</strong> (the "App") you
            agree to the terms below. If you do not agree, do not use the App.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">1. License</h2>
          <p>
            Bilateral Calm grants you a personal, non-transferable, non-exclusive license to use
            the App on any Apple-branded products that you own or control, as permitted by the
            Usage Rules in the Apple Media Services Terms. This license does not allow you to use
            the App on any device that you do not own or control, and you may not distribute or
            make the App available over a network where it could be used by multiple devices at
            once.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">2. Subscriptions</h2>
          <p>
            Bilateral Calm offers auto-renewing subscriptions (Monthly and Annual). Payment is
            charged to your Apple ID account at confirmation of purchase. Your subscription
            automatically renews unless auto-renew is turned off at least 24 hours before the end
            of the current period. Your account will be charged for renewal within 24 hours prior
            to the end of the current period. You can manage subscriptions and turn off
            auto-renewal in your Apple ID Account Settings after purchase.
          </p>
          <p>
            <strong>Title:</strong> Bilateral Calm Premium (Monthly or Annual)<br />
            <strong>Length:</strong> Monthly subscription renews every month. Annual subscription
            renews every year.<br />
            <strong>Price:</strong> Monthly $12.99 USD / month. Annual $69.99 USD / year (≈ $5.83
            per month). A 5-day free trial is available; any unused portion of the trial is
            forfeited if you purchase the subscription during the trial.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">3. Content</h2>
          <p>
            All audio, text, imagery, and software in the App are owned by Bilateral Calm or its
            licensors. You may not copy, redistribute, or create derivative works from the content
            for any purpose other than your personal use of the App.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">
            4. Medical Disclaimer
          </h2>
          <p>
            Bilateral Calm provides wellness practices. It is not a medical device and does not
            diagnose, treat, cure, or prevent any disease. The App is not a substitute for
            professional medical advice. If you are in crisis or experiencing a medical emergency,
            call 988 (US) or your local emergency number.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">5. Termination</h2>
          <p>
            This license is effective until terminated by you or by Bilateral Calm. Your rights
            under this license will terminate automatically without notice if you fail to comply
            with any term. Upon termination of the license, you must cease all use of the App and
            destroy all copies of the App in your possession.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">6. Liability</h2>
          <p>
            To the maximum extent permitted by applicable law, in no event shall Bilateral Calm,
            its affiliates, agents, or licensors be liable for any incidental, special, indirect,
            or consequential damages whatsoever, including any damages for loss of profits, loss
            of data, business interruption, or any other commercial damages or losses, arising out
            of or related to your use of the App.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">7. Privacy</h2>
          <p>
            Our handling of your data is described in the{" "}
            <Link
              to="/privacy"
              className="text-[#3090A8] underline"
              data-testid="terms-privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">8. Governing Law</h2>
          <p>
            These terms are governed by the laws of the State of Minnesota, United States, without
            regard to its conflict of law principles.
          </p>

          <h2 className="text-xl font-semibold text-[#1B2349] mt-8">9. Contact</h2>
          <p>
            Questions about these terms? Email{" "}
            <a
              href="mailto:mscwellness@protonmail.com"
              className="text-[#3090A8] underline underline-offset-2"
              data-testid="terms-contact-email"
            >
              mscwellness@protonmail.com
            </a>
            . Mail is monitored by Mood and Sleep Clinic LLC.
          </p>

          <p className="text-xs text-[#718096] pt-6">Last updated: June 27, 2026</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default TermsOfUsePage;
